import React, { useState, useEffect, RefObject } from 'react';
import styles from './ActiveChatPersonaBar.module.css';
import { exploreIcon, createIcon } from '../../assets/icons';
import CreatePersonaModal from '../utils/CreatePersonaModal';
import SearchPersonaModal from './SearchPersonaModal';
import { api } from '../../api';
import type { PersonaListResponse, CreatePersonaPayload } from '../../api/types';

// Define the PersonaSubmitData interface to match CreatePersonaModal's expected type
interface PersonaSubmitData {
  name: string;
  description: string;
  icon: File | null;
}

interface ChatMember {
  member_type: 'persona' | 'user' | string;
  username: string;
  display_name: string;
  icon: string;
}

interface Chat {
  id: string;
  members?: ChatMember[];
}

interface ActiveChatPersonaBarProps {
  containerRef: RefObject<HTMLDivElement>;
  onPersonaSelect: (persona: PersonaListResponse) => void;
  chat?: Chat;
}

function ActiveChatPersonaBar({ containerRef, onPersonaSelect, chat }: ActiveChatPersonaBarProps): JSX.Element {
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [recentPersonas, setRecentPersonas] = useState<PersonaListResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Get current persona/user from chat members by member_type
  const currentMember = chat?.members?.find(m => 
    m.member_type === 'persona' || m.member_type === 'user'
  );

  useEffect(() => {
    loadRecentPersonas();
  }, []);

  const loadRecentPersonas = async (): Promise<void> => {
    try {
      const personas = await api.personas.list();
      setRecentPersonas(personas);
    } catch (err) {
      console.error('Failed to load personas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (e: React.MouseEvent): void => {
    // Prevent any clicks in the bar from bubbling up
    e.stopPropagation();
  };

  const handleCreatePersona = async (personaData: PersonaSubmitData): Promise<void> => {
    try {
      const newPersona = await api.personas.create({
        display_name: personaData.name,
        icon: personaData.icon, // This is now correctly typed as File | null
        description: personaData.description
      } as CreatePersonaPayload);
      
      // Refresh the list of personas
      await loadRecentPersonas();
      
      // Select the newly created persona
      onPersonaSelect(newPersona);
    } catch (err) {
      console.error('Failed to create persona:', err);
      throw err;
    }
  };

  return (
    <>
      <div ref={containerRef} className={styles.personaBar} onClick={handleClick}>
        <div className={styles.leftSection}>
          <button 
            className={`${styles.iconButton} ${styles.searchIcon}`}
            onClick={() => setShowSearchModal(true)}
          >
            <img src={exploreIcon} alt="Search" />
          </button>
          {!isLoading && recentPersonas
            .filter(persona => persona.id !== currentMember?.username)
            .slice(0, 4)
            .map(persona => (
              <button 
                key={persona.id}
                className={`${styles.iconButton} ${styles.personaIcon}`}
                title={persona.display_name}
                onClick={() => onPersonaSelect(persona)}
              >
                <img src={persona.icon} alt={persona.display_name} />
              </button>
            ))}
          <button 
            className={styles.iconButton}
            onClick={() => setShowCreateModal(true)}
          >
            <img src={createIcon} alt="Create" />
          </button>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.userSection}>
            <button className={`${styles.iconButton} ${styles.userIcon}`}>
              <img 
                src={currentMember?.icon} 
                alt={currentMember?.display_name} 
              />
            </button>
            <span className={styles.userName}>
              {currentMember?.display_name}
            </span>
          </div>
        </div>
      </div>

      <CreatePersonaModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreatePersona}
      />

      <SearchPersonaModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelect={onPersonaSelect}
      />
    </>
  );
}

export default ActiveChatPersonaBar; 